/**
 * Reconciliation Detail Page
 * 
 * Admin interface for detailed conflict resolution
 * 
 * Features:
 * - Side-by-side comparison of conflicting data
 * - Highlight differing fields
 * - Manual conflict resolution
 * - Change primary broker
 * - View reconciliation history
 * 
 * Access: Admin-only (requires 'admin' scope)
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { services } from '../../../../services/api';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { 
  FiArrowLeft, 
  FiRefreshCw, 
  FiCheck,
  FiX,
  FiEdit,
  FiAlertCircle,
  FiAlertTriangle
} from 'react-icons/fi';

const ReconciliationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [conflictDetail, setConflictDetail] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState(null);

  // Check admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      showSnackbar('Access denied. Admin privileges required.', 'error');
      navigate('/Admin/Reconciliation');
    }
  }, [user, showSnackbar, navigate]);

  // Fetch conflict detail on mount
  useEffect(() => {
    fetchConflictDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchConflictDetail = async () => {
    try {
      setLoading(true);
      const response = await services.instruments.admin.getConflictDetail(id);
      
      if (response.data && response.data.status === 'success') {
        setConflictDetail(response.data.data);
        setSelectedBroker(response.data.data.primary_broker_id);
      }
    } catch (error) {
      console.error('Error fetching conflict detail:', error);
      showSnackbar(error.message || 'Failed to load conflict details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resolveConflict = async (action, brokerId = null) => {
    try {
      setResolving(true);

      const payload = {
        action: action, // 'keep_current', 'accept_broker', 'manual_override'
        broker_id: brokerId,
        ...(action === 'manual_override' && { 
          override_data: conflictDetail.master_data // Can be modified for manual override
        })
      };

      const response = await services.instruments.admin.resolveConflict(id, payload);
      
      if (response.data && response.data.status === 'success') {
        showSnackbar('Conflict resolved successfully', 'success');
        navigate('/Admin/Reconciliation');
      }
    } catch (error) {
      console.error('Error resolving conflict:', error);
      showSnackbar(error.message || 'Failed to resolve conflict', 'error');
    } finally {
      setResolving(false);
    }
  };

  const changePrimaryBroker = async () => {
    if (!selectedBroker || selectedBroker === conflictDetail.primary_broker_id) {
      showSnackbar('Please select a different broker', 'warning');
      return;
    }

    try {
      setResolving(true);

      const response = await services.instruments.admin.changePrimaryBroker(id, {
        new_primary_broker_id: selectedBroker
      });
      
      if (response.data && response.data.status === 'success') {
        showSnackbar('Primary broker changed successfully', 'success');
        fetchConflictDetail(); // Refresh data
      }
    } catch (error) {
      console.error('Error changing primary broker:', error);
      showSnackbar(error.message || 'Failed to change primary broker', 'error');
    } finally {
      setResolving(false);
    }
  };

  // Highlight differences between two data objects
  // const highlightDifferences = (field, masterValue, brokerValue) => {
  //   if (masterValue === brokerValue || (!masterValue && !brokerValue)) {
  //     return null; // No difference
  //   }
  //   return 'different';
  // };

  // Format field value for display
  const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return value.toString();
  };

  if (loading) {
    return (
      <div className="m-2 md:m-6 p-4 md:p-8 bg-bg-2 rounded-3xl">
        <div className="flex items-center justify-center h-64">
          <FiRefreshCw className="animate-spin text-4xl text-blue-500" />
          <span className="ml-3 text-lg">Loading conflict details...</span>
        </div>
      </div>
    );
  }

  if (!conflictDetail) {
    return (
      <div className="m-2 md:m-6 p-4 md:p-8 bg-bg-2 rounded-3xl">
        <div className="text-center py-16">
          <FiAlertCircle className="mx-auto text-6xl text-fg-3 mb-4" />
          <p className="text-xl text-fg-2">Conflict not found</p>
          <button
            type="button"
            onClick={() => navigate('/Admin/Reconciliation')}
            className="btn btn-primary mt-4"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const { master_data, broker_data, conflicting_fields, validation_issues } = conflictDetail;

  return (
    <div className="m-2 md:m-6 p-4 md:p-8 bg-bg-2 rounded-3xl">
      {/* Header */}
      <div className="mb-6 flex justify-between items-start">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/Admin/Reconciliation')}
            className="p-2 hover:bg-bg-3 rounded-lg transition-colors"
          >
            <FiArrowLeft className="text-2xl" />
          </button>
          
          <div>
            <h1 className="ds-h1 tracking-tight text-slate-900 dark:text-gray-200">
              {master_data.exchange} : {master_data.symbol}
            </h1>
            <p className="text-fg-2 mt-1">
              {master_data.instrument_type} • {master_data.name}
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={fetchConflictDetail}
            disabled={resolving}
            className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
          >
            <FiRefreshCw className={resolving ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Status Badge */}
      <div className="mb-6">
        <span className={`ds-badge lg ${
          conflictDetail.reconciliation_status === 'conflict_needs_review' ? 'loss' : 'info'
        }`}>
          {conflictDetail.reconciliation_status === 'conflict_needs_review'
            ? '⚠️ Needs Review'
            : '✓ Auto-Resolved'
          }
        </span>
      </div>

      {/* Validation Issues */}
      {validation_issues && validation_issues.length > 0 && (
        <div className="mb-6 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <FiAlertTriangle className="text-orange-600 dark:text-orange-400 text-xl" />
            <h3 className="text-orange-900 dark:text-orange-100 font-semibold">
              Validation Issues
            </h3>
          </div>
          <ul className="space-y-1">
            {validation_issues.map((issue, idx) => (
              <li key={idx} className="text-orange-700 dark:text-orange-300 text-sm">
                • {issue.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Primary Broker Selection */}
      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-blue-900 dark:text-blue-100 font-semibold mb-1">
              Primary Data Source
            </h3>
            <p className="text-blue-700 dark:text-blue-300 text-sm">
              Current: <strong>{conflictDetail.primary_broker || 'Not Set'}</strong>
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={selectedBroker || ''}
              onChange={(e) => setSelectedBroker(e.target.value)}
              className="px-3 py-2 border border-blue-300 dark:border-blue-700 rounded-lg 
                       bg-white dark:bg-bg-3 text-fg-1"
            >
              <option value="">Select Broker...</option>
              {Object.keys(broker_data || {}).map(brokerId => (
                <option key={brokerId} value={brokerId}>
                  {brokerId}
                </option>
              ))}
            </select>
            
            <button
              type="button"
              onClick={changePrimaryBroker}
              disabled={resolving || !selectedBroker || selectedBroker === conflictDetail.primary_broker_id}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Change Primary
            </button>
          </div>
        </div>
      </div>

      {/* Conflicting Fields Summary */}
      {conflicting_fields && conflicting_fields.length > 0 && (
        <div className="mb-6">
          <h3 className="ds-h3 text-fg-1 mb-3">
            Conflicting Fields ({conflicting_fields.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {conflicting_fields.map(field => (
              <span
                key={field}
                className="ds-badge loss sm"
              >
                {field}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Side-by-Side Comparison */}
      <div className="mb-6">
        <h3 className="ds-h3 text-fg-1 mb-4">
          Data Comparison
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Current Master Data */}
          <div className="border-2 border-green-300 dark:border-green-700 rounded-lg p-4 bg-green-50 dark:bg-green-900/10">
            <h4 className="ds-h4 text-green-800 dark:text-green-200 mb-4 flex items-center gap-2">
              <FiCheck className="text-xl" />
              Current Master Data
            </h4>
            
            <div className="space-y-3">
              {Object.entries(master_data).map(([key, value]) => {
                const isDifferent = conflicting_fields?.includes(key);
                
                return (
                  <div 
                    key={key}
                    className={`p-2 rounded ${
                      isDifferent 
                        ? 'bg-red-100 dark:bg-red-900/30 border-l-4 border-red-500' 
                        : 'bg-bg-2'
                    }`}
                  >
                    <div className="text-xs text-fg-2 font-semibold uppercase">
                      {key}
                    </div>
                    <div className="text-sm text-fg-1 mt-1 font-mono">
                      {formatValue(value)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Broker Data Sources */}
          <div className="space-y-4">
            {Object.entries(broker_data || {}).map(([brokerId, data]) => (
              <div 
                key={brokerId}
                className="border-2 border-blue-300 dark:border-blue-700 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/10"
              >
                <h4 className="ds-h4 text-blue-800 dark:text-blue-200 mb-4">
                  {brokerId}
                  {brokerId === conflictDetail.primary_broker_id && (
                    <span className="ds-badge info xs ml-2">
                      PRIMARY
                    </span>
                  )}
                </h4>
                
                <div className="space-y-3">
                  {Object.entries(data).map(([key, value]) => {
                    const isDifferent = conflicting_fields?.includes(key);
                    
                    return (
                      <div 
                        key={key}
                        className={`p-2 rounded ${
                          isDifferent 
                            ? 'bg-yellow-100 dark:bg-yellow-900/30 border-l-4 border-yellow-500' 
                            : 'bg-bg-2'
                        }`}
                      >
                        <div className="text-xs text-fg-2 font-semibold uppercase">
                          {key}
                        </div>
                        <div className="text-sm text-fg-1 mt-1 font-mono">
                          {formatValue(value)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Resolution Actions */}
      <div className="border-t border-[var(--divider)] pt-6">
        <h3 className="ds-h3 text-fg-1 mb-4">
          Resolution Actions
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Keep Current */}
          <button
            onClick={() => resolveConflict('keep_current')}
            disabled={resolving}
            className="p-6 border-2 border-green-300 dark:border-green-700 rounded-lg 
                     hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiCheck className="text-3xl text-green-600 dark:text-green-400 mx-auto mb-3" />
            <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">
              Keep Current Master
            </h4>
            <p className="text-sm text-green-600 dark:text-green-400">
              Maintain current master data as-is
            </p>
          </button>

          {/* Accept Broker Data */}
          {Object.keys(broker_data || {}).map(brokerId => (
            <button
              key={brokerId}
              onClick={() => resolveConflict('accept_broker', brokerId)}
              disabled={resolving}
              className="p-6 border-2 border-blue-300 dark:border-blue-700 rounded-lg 
                       hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiCheck className="text-3xl text-blue-600 dark:text-blue-400 mx-auto mb-3" />
              <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                Accept {brokerId}
              </h4>
              <p className="text-sm text-blue-600 dark:text-blue-400">
                Use {brokerId} data as master
              </p>
            </button>
          ))}

          {/* Manual Override */}
          <button
            onClick={() => {
              showSnackbar('Manual override UI coming soon', 'info');
            }}
            disabled={resolving}
            className="p-6 border-2 border-purple-300 dark:border-purple-700 rounded-lg 
                     hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiEdit className="text-3xl text-purple-600 dark:text-purple-400 mx-auto mb-3" />
            <h4 className="font-semibold text-purple-800 dark:text-purple-200 mb-2">
              Manual Override
            </h4>
            <p className="text-sm text-purple-600 dark:text-purple-400">
              Enter custom data manually
            </p>
          </button>

          {/* Reject All */}
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to reject this instrument? This will mark it for review.')) {
                showSnackbar('Reject functionality coming soon', 'info');
              }
            }}
            disabled={resolving}
            className="p-6 border-2 border-red-300 dark:border-red-700 rounded-lg 
                     hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiX className="text-3xl text-red-600 dark:text-red-400 mx-auto mb-3" />
            <h4 className="font-semibold text-red-800 dark:text-red-200 mb-2">
              Reject All
            </h4>
            <p className="text-sm text-red-600 dark:text-red-400">
              Mark for further review
            </p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReconciliationDetail;
